import { FC } from "react";
import { Box, Button } from "@mui/material";
import { Colors } from "../../../../configs";
import { useAppDispatch, useAppSelector } from "../../../../hooks";
import { cloneDeep } from "lodash";
import { CartItem } from "../../../../typings/interfaces/pos/cart-item";
import { updateCartSessionItem } from "../../../../redux/actions/cartAction";
import { sortArray } from "../../../../util/array-functions";
import { AlterationSubCategoryResponse } from "../../../../typings/interfaces/responses/alteration-sub-category-response";
import { IAlterationItem } from "../../../../typings/interfaces/pos/alterationItem";
import { CustomButton } from "../../../atoms";
import { SwiperSlide } from "swiper/react";
import PosSwiper from "../pos-swiper/PosSwiper";
import useAlterationPricing from "../../../../hooks/useAlterationPricing";

export interface IGarmentModificationSectionProps {}

const COLUMNS = 3;
const ROWS = 4;
const TYPES_PER_PAGE = COLUMNS * ROWS;
const BUTTON_WIDTH = "7.14rem";
const BUTTON_HEIGHT = "3.78571429rem";
const GAP = "0.5rem";
const MENU_WIDTH = `calc(${COLUMNS} * ${BUTTON_WIDTH} + ${COLUMNS - 1} * ${GAP})`;

const GarmentModificationSection: FC<IGarmentModificationSectionProps> = () => {
  const { selectedCartItem } = useAppSelector((state) => state.cart);
  const { alterationModificationType } = useAlterationPricing();
  const dispatch = useAppDispatch();

  // Second-sub-categories are already loaded once at app init
  // (useAppInitializer) and this screen renders from `alterationModificationType`
  // (getPricing, scoped to the item's service by useAlterationPricing), not from that response - re-dispatching
  // getSecondSubCategoryAction here on every mount/service-type change was a
  // redundant, unused network fetch that added a full round-trip of latency
  // every time a cashier opened the Alteration category.

  const handleOnSelectType = (
    alterationSubCat: AlterationSubCategoryResponse
  ) => {
    let cartItem: CartItem;
    if (selectedCartItem) {
      cartItem = cloneDeep(selectedCartItem);

      // Check for incomplete alteration items and update if found
      const uncompletedItemIndex = cartItem.alterationItems.findIndex(
        (item) => !item.alterationSubCategory || !item.alterationPrice
      );

      if (uncompletedItemIndex !== -1) {
        cartItem.alterationItems[uncompletedItemIndex].alterationSubCategory =
          alterationSubCat;
      } else {
        // If no incomplete item is found, add a new one
        const newItem: IAlterationItem = {
          alterationSubCategory: alterationSubCat,
        };
        cartItem.alterationItems.push(newItem);
      }

      dispatch(updateCartSessionItem({ cartItem }));
    }
  };

  const getIncompleteItemType = (cartItem: CartItem | null) => {
    const incompleteItem = cartItem?.alterationItems.find(
      (item) => !item.alterationSubCategory || !item.alterationPrice
    );
    return incompleteItem ? incompleteItem.alterationSubCategory?.id : null;
  };

  const incompleteItemType = getIncompleteItemType(selectedCartItem);

  const garmentModificationSorted = sortArray({
    array: alterationModificationType || [],
    sortByKey: "materialName",
  });

  // Pages of 3 columns x 4 rows, swiped like the garment/style/fabric menus.
  // Rows used to grow to ceil(n / 4) buttons, so past 12 types the 4th column
  // fell outside the panel with no visible scrollbar.
  const pages = Array.from(
    { length: Math.ceil(garmentModificationSorted.length / TYPES_PER_PAGE) },
    (_, page) =>
      garmentModificationSorted.slice(page * TYPES_PER_PAGE, (page + 1) * TYPES_PER_PAGE)
  );

  return (
    <Box
      sx={{
        flex: 1,
        flexShrink: 1,
        background: Colors.BACKGROUND_GRAY,
        borderRadius: "0.71rem",
        padding: "0.928571429rem",
        height: "20.7857143rem",
        maxHeight: "20.7857143rem",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <PosSwiper style={{ width: MENU_WIDTH }} spaceBetween={8} direction="horizontal">
        {pages.map((pageTypes, pageIndex) => (
          <SwiperSlide
            key={pageIndex}
            style={{
              display: "grid",
              gridTemplateColumns: `repeat(${COLUMNS}, ${BUTTON_WIDTH})`,
              gridAutoRows: BUTTON_HEIGHT,
              gap: GAP,
              alignContent: "start",
              width: MENU_WIDTH,
              height: "18.1428572rem",
            }}
          >
            {pageTypes.map((modification: any) => {
              const isSelected = incompleteItemType === modification.materialId;
              return (
                <CustomButton
                  name="alteration-type-button"
                  key={modification.materialId}
                  onClick={() =>
                    handleOnSelectType({
                      id: modification.materialId,
                      name: modification.materialName,
                      deleted: false,
                    })
                  }
                  sx={{
                    width: BUTTON_WIDTH,
                    minWidth: BUTTON_WIDTH,
                    height: BUTTON_HEIGHT,
                    padding: "0 0.29rem",
                    background: isSelected ? Colors.SECONDARY : Colors.MAIN_GRAY,
                    borderRadius: "0.71rem",
                    textTransform: "capitalize",
                    color: Colors.WHITE,
                    fontSize: "var(--fs-2xs)",
                    lineHeight: 1.15,
                    // Long names ("Miscellaneous") wrap/hyphenate inside the
                    // button instead of running past its edges
                    whiteSpace: "normal",
                    hyphens: "auto",
                    // Keep the selected colour on hover: touch screens keep
                    // :hover after a tap, which used to grey out the pick
                    "&:hover": {
                      background: `${isSelected ? Colors.SECONDARY : Colors.MAIN_GRAY} !important`,
                    },
                  }}
                >
                  {modification.materialName}
                </CustomButton>
              );
            })}
          </SwiperSlide>
        ))}
      </PosSwiper>
    </Box>
  );
};

export default GarmentModificationSection;
